import * as Sentry from "@sentry/react";
import * as client from "./client";

// This type should be used in functions that require a client ID but don't need
// to inspect it, for example they just need to check a feature.
export type ClientId = string & { readonly __tag: unique symbol };
export const makeClientId = (clientId: string): ClientId =>
  clientId as ClientId;

// This type should be used in cases where you want to verify something is a
// known client, for example hard coded data objects.
export type KnownClientId = (typeof KNOWN_CLIENT_IDS)[number];
export type KnownDevClientId = (typeof KNOWN_DEV_CLIENT_IDS)[number];

const KNOWN_CLIENT_IDS = ["pantheon", "luminor"] as const;
const KNOWN_DEV_CLIENT_IDS = ["luminor_dev"] as const;
const DEV_CLIENT_IDS_TO_PROD_CLIENT_IDS: { [dev: string]: KnownClientId } = {
  luminor_dev: "luminor",
} as const;

type FeatureConfig =
  | true // enable for whole client
  | {
      // enable for specific users. These terms are OR'd
      enable_for?: string[];
      enable_for_admin?: boolean;
    };

// User check and LOB check are AND'd together.
type FeatureFlags = {
  [K in KnownClientId]?: FeatureConfig;
} & { lobs?: string[] };

function inferKeys<const T extends Record<string, FeatureFlags>>(features: T) {
  for (const clients of Object.values(features)) {
    for (const client_id of Object.keys(clients)) {
      if (
        client_id !== "lobs" &&
        !(KNOWN_CLIENT_IDS as readonly string[]).includes(client_id)
      ) {
        throw Error(`Bad feature client_id ${client_id}`);
      }
    }
  }
  return features;
}
const FEATURES = inferKeys({
  "avatar-menu": {
    pantheon: true,
    luminor: true,
  },
  "handler-column": {
    pantheon: true,
  },
  "location-count-not-include-unique-buildings": {},
  // If you enable this for other clients, make sure the Dashboard
  // logic still works. It assumes the use of a precomputed_metadata field
  "appetite-filtering": { luminor: true },
  "layers-card": {
    pantheon: true,
    luminor: true,
  },
  "loss-run-card": {
    pantheon: true,
    luminor: true,
  },
  "loss-control-reports-card": {
    pantheon: true,
  },
  "policy-review-questions": {
    pantheon: true,
  },
  "underwriter-handler-separation": {},
  "analytics-page": {
    pantheon: true,
    luminor: true,
  },
  "show-target-premium-in-general-tab": {
    pantheon: true,
  },
  "show-brokerage": {
    pantheon: true,
    luminor: true,
  },
  "show-general-policy-fields": {},
  comments: {
    luminor: true,
  },
  "gray-out-declined-rows": {},
  "status-column-on-left": {
    luminor: true,
  },
  "show-states": {},
  "flag-no-location-in-insured-mailing-state": {},
  "frozen-sov-header": {
    luminor: true,
  },
  "use-named-insured-as-company-name": {
    luminor: true,
  },
  "assignee-named-underwriter": {},
  "target-premium-in-dashboard": {},
  "export-report": { luminor: true },
  "disable-flag-review-question": { luminor: true, lobs: [] },
  "sov-overview-card": {
    luminor: true,
  },
  "policy-questions-card": {
    luminor: true,
  },
  "policy-fields-card": {
    luminor: true,
  },
  "limits-card": {
    luminor: { enable_for_admin: true },
  },
  "deductible-card": {
    luminor: { enable_for_admin: true },
  },
  "template-management": {
    luminor: { enable_for_admin: true },
  },
  "brokerage-analytics": {
    pantheon: true,
  },
  "crm-brokerage-preferred": {},
  "broker-export": {
    pantheon: true,
  },
  "policy-fly-integration": {
    luminor: true,
  },
  "submission-history": {
    luminor: { enable_for_admin: true },
    pantheon: true,
  },
  "edit-team": {
    luminor: { enable_for_admin: true },
    pantheon: { enable_for_admin: true },
  },
  "rerun-cancellation": {
    pantheon: true,
    luminor: { enable_for_admin: true },
  },
  "reprocess-button": {
    luminor: { enable_for_admin: true },
    pantheon: true,
  },
  "tiv-reconciliation-functions": {
    pantheon: true,
    luminor: { enable_for_admin: true },
  },
  "cat-model-override-allowed": {
    luminor: {
      enable_for: [
        "andrew.hannon@luminorrisk.com",
        "greg.karabinos@luminorrisk.com",
        "sean.stephens@luminorrisk.com",
        "john.karabinos@luminorrisk.com",
      ],
    },
  },
  "show-icm-start-button": {
    luminor: {
      enable_for: [
        "andrew.hannon@luminorrisk.com",
        "greg.karabinos@luminorrisk.com",
        "sean.stephens@luminorrisk.com",
        "john.karabinos@luminorrisk.com",
      ],
    },
  },
  "show-rms-legacy-tab": {
    luminor: true,
  },
  "large-cat-jobs-allowed": {
    luminor: {
      enable_for: [
        "andrew.hannon@luminorrisk.com",
        "sean.stephens@luminorrisk.com",
        "john.karabinos@luminorrisk.com",
        "greg.karabinos@luminorrisk.com",
      ],
    },
  },
  "rms-rerun-financials": {
    luminor: {
      enable_for: [
        "dev@luminorrisk.com",
        "andrew.hannon@luminorrisk.com",
        "sean.stephens@luminorrisk.com",
        "john.karabinos@luminorrisk.com",
        "greg.karabinos@luminorrisk.com",
      ],
    },
  },
  "show-nearmap-tab": {
    luminor: {
      enable_for: [
        "dev@luminorrisk.com",
        "andrew.hannon@luminorrisk.com",
        "sean.stephens@luminorrisk.com",
        "john.karabinos@luminorrisk.com",
        "greg.karabinos@luminorrisk.com",
      ],
    },
  },
});

export type Feature = keyof typeof FEATURES;

export const featureEnabled = (
  clientId: ClientId,
  feature: Feature,
  args: { lob?: string; user?: client.User } = {}
): boolean => {
  const { lob, user } = args;
  const prodId = DEV_CLIENT_IDS_TO_PROD_CLIENT_IDS[clientId];
  if (prodId) {
    clientId = makeClientId(prodId);
  }

  const featureFlags: FeatureFlags = FEATURES[feature];
  if (!featureFlags) {
    return false;
  }

  const lobs = featureFlags["lobs"];
  if (lobs && lobs.length > 0) {
    if (!lob) {
      Sentry.captureMessage(`Missing lob for feature ${feature}`);
    }

    if (!lob || !(lobs as string[]).includes(lob)) {
      return false;
    }
  }

  const config = (featureFlags as Record<string, FeatureConfig | undefined>)[
    clientId
  ];
  if (config === true) {
    return true;
  } else if (config === undefined) {
    return false;
  } else {
    if (!("user" in args)) {
      Sentry.captureMessage(`Missing user for feature ${feature}`);
      return false;
    }

    return [
      config.enable_for?.includes(user?.email ?? ""),
      config.enable_for_admin && user?.is_client_admin,
    ].some((c) => c);
  }
};

export function perClient<T>(map: { [clientId in KnownClientId]?: T }): {
  [clientId: ClientId]: T | undefined;
} {
  return {
    ...map,
    ...Object.fromEntries(
      Object.entries(DEV_CLIENT_IDS_TO_PROD_CLIENT_IDS).map(([dev, prod]) => [
        dev,
        map[prod],
      ])
    ),
  };
}

export function perClientWithLob<T>(map: {
  [clientId in KnownClientId]?: (lob: string) => T;
}): (lob: string) => { [clientId: ClientId]: T | undefined } {
  return (lob: string) => {
    const resolvedMap: { [clientId in KnownClientId]?: T } = {};

    for (const [clientId, value] of Object.entries(map)) {
      resolvedMap[clientId as KnownClientId] = value(lob);
    }

    return {
      ...resolvedMap,
      ...Object.fromEntries(
        Object.entries(DEV_CLIENT_IDS_TO_PROD_CLIENT_IDS).map(([dev, prod]) => [
          dev,
          resolvedMap[prod],
        ])
      ),
    };
  };
}

export const resolveProductionClientId = (clientId: ClientId): ClientId => {
  const prod = DEV_CLIENT_IDS_TO_PROD_CLIENT_IDS[clientId];
  return prod ? makeClientId(prod) : clientId;
};
